/**
 * Eine Position in der Staffing-Maske (#158). Eingeklappt eine Zeile (Status · Stufe · VK-Satz ·
 * PT gesamt · aktive Namen), die ganze Zeile klappt auf. Aufgeklappt: Positionsfelder und
 * Besetzungen (#163: die Position ist der Bedarf). Dazu das Formular „Position hinzufügen" (F4: alle Felder in einem Rutsch).
 * KI-Vorschläge kommen hier nie an — die Maske zeigt sie nur im Kasten „zu bestätigen" (#163).
 */
import type { MaskePosition, PreisEinheit } from '@projektverwaltung/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { useConfirm } from '../confirm-dialog.js';
import { EINGABE_RAHMEN, InlineFeld } from '../inline-feld.js';
import { BesetzungZeile } from './besetzung-zeile.js';
import {
  useMaske,
  useMaskeKnopf,
  useMaskeSchreiben,
  usePflicht,
  usePreisText,
} from './maske-kontext.js';
import { NeueBesetzung } from './neue-besetzung.js';
import { useZiehen } from './nicht-zugeordnet.js';
import { StatusBadge } from './status-badge.js';

type PatchPosition = Parameters<typeof api.patchBeauftragtePosition>[1];

const KNOPF = 'text-xs hover:underline';

function Feld({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-3)' }}>
        {titel}
      </div>
      {children}
    </div>
  );
}

/** Namen der aktiven (nicht ausgeplanten) Besetzungen der Position. */
export function aktiveNamen(position: MaskePosition): string[] {
  return position.besetzungen.filter((b) => b.status !== 'ausgeplant').map((b) => b.person_name);
}

/** @param anfangsOffen aufgeklappt einhängen (eben angelegte Position, Spec 5 „neue aufgeklappte Zeile"). */
export function PositionsZeile({
  position,
  anfangsOffen = false,
}: {
  position: MaskePosition;
  anfangsOffen?: boolean;
}) {
  const { t } = useTranslation();
  const { rechte } = useMaske();
  const schreiben = useMaskeSchreiben();
  const knopf = useMaskeKnopf();
  const confirmAction = useConfirm();
  const pflicht = usePflicht();
  const preisText = usePreisText();
  const [offen, setOffen] = useState(anfangsOffen);
  const [besetzen, setBesetzen] = useState(false);
  const ziehen = useZiehen();
  const [ablegenHier, setAblegenHier] = useState(false);
  const id = position.beauftragte_position_id;
  const kann = rechte.canManage;
  const speichern = (input: PatchPosition) =>
    schreiben(() => api.patchBeauftragtePosition(id, input));
  const namen = aktiveNamen(position);
  const einheitOptionen: { value: PreisEinheit; label: string }[] = [
    { value: 'stunde', label: t('v2.staffing_maske.einheit_stunde') },
    { value: 'tag', label: t('v2.staffing_maske.einheit_tag') },
  ];

  // Ablegeziel für Einträge aus „Nicht zugeordnet" (#158). `ziehen` gibt es nur mit
  // `canResolve`; gezogen werden nur Besetzungen. Ohne gezogenen Eintrag nimmt die Zeile nichts an.
  const ablegen = ziehen
    ? {
        onDragOver: (e: React.DragEvent) => {
          if (!ziehen.gezogen) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          setAblegenHier(true);
        },
        onDragLeave: () => setAblegenHier(false),
        onDrop: (e: React.DragEvent) => {
          setAblegenHier(false);
          const eintrag = ziehen.gezogen;
          if (!eintrag) return;
          e.preventDefault();
          ziehen.setGezogen(null);
          ziehen.zuordnen(eintrag, id);
        },
      }
    : {};

  return (
    <div
      data-testid={`position-${id}`}
      style={{
        borderBottom: '1px solid var(--line-soft)',
        outline: ablegenHier ? '2px dashed var(--primary)' : undefined,
        background: ablegenHier ? 'var(--primary-soft)' : undefined,
      }}
      {...ablegen}
    >
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => setOffen((v) => !v)}
        className="flex w-full min-w-0 items-center gap-3 whitespace-nowrap px-2 py-1.5 text-left text-sm hover:bg-[var(--surface-2)]"
      >
        <span
          className="shrink-0 text-xs"
          style={{
            color: 'var(--ink-3)',
            transform: offen ? 'none' : 'rotate(-90deg)',
            transition: 'transform 120ms',
          }}
        >
          ▾
        </span>
        <StatusBadge status={position.status_anzeige} />
        <span className="shrink-0 font-medium" style={{ color: 'var(--ink)' }}>
          {position.stufe}
        </span>
        <span className="shrink-0" style={{ color: 'var(--ink-2)' }}>
          {preisText(position.preis, position.preis_einheit)}
        </span>
        <span className="shrink-0" style={{ color: 'var(--ink-2)' }}>
          {position.anzahl_pt === null
            ? '—'
            : t('v2.staffing_maske.pt', { n: position.anzahl_pt.toLocaleString('de-DE') })}
        </span>
        <span className="min-w-0 truncate" style={{ color: 'var(--ink-3)' }}>
          {namen.length > 0 ? namen.join(', ') : t('v2.staffing_maske.keine_namen')}
        </span>
      </button>
      {offen && (
        <div className="space-y-2 px-2 pb-3 pl-6">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-4">
            <Feld titel={t('v2.staffing_maske.feld.stufe')}>
              <InlineFeld<string>
                art="text"
                wert={position.stufe}
                kannBearbeiten={kann}
                label={t('v2.staffing_maske.feld.stufe')}
                onSpeichern={(neu) => speichern({ stufe: pflicht(neu) })}
              />
            </Feld>
            <Feld titel={t('v2.staffing_maske.feld.preis')}>
              <div className="flex gap-1">
                <InlineFeld<number>
                  art="zahl"
                  wert={position.preis}
                  kannBearbeiten={kann}
                  label={t('v2.staffing_maske.feld.preis')}
                  onSpeichern={(neu) => speichern({ preis: neu })}
                />
                <InlineFeld<PreisEinheit>
                  art="auswahl"
                  wert={position.preis_einheit}
                  optionen={einheitOptionen}
                  kannBearbeiten={kann}
                  label={t('v2.staffing_maske.feld.einheit')}
                  onSpeichern={(neu) => speichern({ preis_einheit: pflicht(neu) })}
                />
              </div>
            </Feld>
            <Feld titel={t('v2.staffing_maske.feld.pt_gesamt')}>
              <InlineFeld<number>
                art="zahl"
                wert={position.anzahl_pt}
                kannBearbeiten={kann}
                label={t('v2.staffing_maske.feld.pt_gesamt')}
                onSpeichern={(neu) => speichern({ anzahl_pt: neu })}
              />
            </Feld>
            <div className="flex items-start justify-end gap-3">
              {kann && (
                <button
                  type="button"
                  className={KNOPF}
                  style={{ color: 'var(--danger)' }}
                  onClick={() =>
                    void confirmAction({
                      title: t('v2.staffing_maske.position_loeschen_titel'),
                      body: t('v2.staffing_maske.position_loeschen_text'),
                      confirmLabel: t('v2.staffing_maske.position_loeschen'),
                      tone: 'danger',
                    }).then((ok) => {
                      if (ok) knopf(() => api.deleteBeauftragtePosition(id));
                    })
                  }
                >
                  {t('v2.staffing_maske.position_loeschen', 'Position löschen')}
                </button>
              )}
            </div>
          </div>
          <Feld titel={t('v2.staffing_maske.feld.notiz_position')}>
            <InlineFeld<string>
              art="textfeld"
              wert={position.notiz ?? null}
              kannBearbeiten={kann}
              label={t('v2.staffing_maske.feld.notiz_position')}
              onSpeichern={(neu) => speichern({ notiz: neu })}
            />
          </Feld>
          <div>
            <div className="text-xs font-medium" style={{ color: 'var(--ink-2)' }}>
              {t('v2.staffing_maske.besetzungen', 'Besetzungen')}
            </div>
            {position.besetzungen.length === 0 && !besetzen && (
              <div className="text-xs" style={{ color: 'var(--ink-3)' }}>
                {t('v2.staffing_maske.keine_besetzung')}
              </div>
            )}
            {position.besetzungen.map((b) => (
              <BesetzungZeile key={b.staffing_besetzung_id} b={b} />
            ))}
            {besetzen ? (
              <NeueBesetzung positionId={id} onFertig={() => setBesetzen(false)} />
            ) : (
              rechte.canResolve && (
                <button
                  type="button"
                  className={KNOPF}
                  style={{ color: 'var(--primary)' }}
                  onClick={() => setBesetzen(true)}
                >
                  {t('v2.staffing_maske.besetzen', '+ Person besetzen')}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Eingabe -> Zahl: leer = `null`, ungültig/negativ = `undefined` (sperrt „Anlegen"). */
function zahlAus(roh: string): number | null | undefined {
  const s = roh.trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * Formular „Position hinzufügen" (F4): Stufe (Pflicht), Satz + Einheit, PT gesamt,
 * Notiz — ein Knopf „Anlegen" schickt EINEN Aufruf mit allen Feldern, ✕ verwirft. `onAngelegt`
 * meldet die neue Id noch vor dem Neuladen — die Maske hängt die Zeile dann aufgeklappt ein.
 */
export function NeuePositionZeile({
  onFertig,
  onAngelegt,
}: {
  onFertig: () => void;
  onAngelegt?: (positionId: string) => void;
}) {
  const { t } = useTranslation();
  const { projectId } = useMaske();
  const knopf = useMaskeKnopf();
  const [stufe, setStufe] = useState('');
  const [preis, setPreis] = useState('');
  const [einheit, setEinheit] = useState<PreisEinheit>('stunde');
  const [pt, setPt] = useState('');
  // Vorlage der kfm. Leitung (Start, Aufgaben, Skills, Umfang pro Woche) — nur beim Neuanlegen.
  const vorlage = t('v2.staffing_maske.notiz_vorlage');
  const [notiz, setNotiz] = useState(vorlage);
  // Fix-Runde 1 (c): gesperrt, solange der Aufruf läuft — kein doppeltes Anlegen.
  const [laeuft, setLaeuft] = useState(false);
  const preisWert = zahlAus(preis);
  const ptWert = zahlAus(pt);
  const vollstaendig = stufe.trim() !== '' && preisWert !== undefined && ptWert !== undefined;
  const eingabe = `rounded px-2 py-1 text-sm ${EINGABE_RAHMEN}`;
  const neu = (feld: string) => t('v2.staffing_maske.neue_position_feld', { feld });

  const anlegen = () => {
    const input: Parameters<typeof api.createBeauftragtePosition>[1] = {
      stufe: stufe.trim(),
      preis_einheit: einheit,
    };
    if (typeof preisWert === 'number') input.preis = preisWert;
    if (typeof ptWert === 'number') input.anzahl_pt = ptWert;
    // Unveränderte Vorlage (nur Leerraum-Unterschiede) ist keine Notiz.
    const leer = (x: string) => x.replace(/\s+/g, '');
    input.notiz = leer(notiz) === leer(vorlage) || notiz.trim() === '' ? null : notiz.trim();
    setLaeuft(true);
    knopf(async () => {
      try {
        const { item } = await api.createBeauftragtePosition(projectId, input);
        onAngelegt?.(item.beauftragte_position_id);
        onFertig();
      } finally {
        setLaeuft(false);
      }
    });
  };

  return (
    <fieldset
      aria-label={t('v2.staffing_maske.neue_position', 'Neue Position')}
      className="flex flex-wrap items-end gap-2 px-2 py-2"
      style={{ borderBottom: '1px solid var(--line-soft)', background: 'var(--surface-2)' }}
    >
      <span className="self-center text-xs font-medium" style={{ color: 'var(--ink-2)' }}>
        {t('v2.staffing_maske.neue_position', 'Neue Position')}
      </span>
      <input
        type="text"
        aria-label={t('v2.staffing_maske.neue_position_stufe', 'Stufe der neuen Position')}
        placeholder={t('v2.staffing_maske.feld.stufe')}
        className={`${eingabe} w-48`}
        value={stufe}
        onChange={(e) => setStufe(e.target.value)}
      />
      <input
        type="text"
        inputMode="decimal"
        aria-label={neu(t('v2.staffing_maske.feld.preis'))}
        placeholder={t('v2.staffing_maske.feld.preis')}
        className={`${eingabe} w-24`}
        value={preis}
        onChange={(e) => setPreis(e.target.value)}
      />
      <select
        aria-label={neu(t('v2.staffing_maske.feld.einheit'))}
        className={eingabe}
        value={einheit}
        onChange={(e) => setEinheit(e.target.value as PreisEinheit)}
      >
        <option value="stunde">{t('v2.staffing_maske.einheit_stunde')}</option>
        <option value="tag">{t('v2.staffing_maske.einheit_tag')}</option>
      </select>
      <input
        type="text"
        inputMode="decimal"
        aria-label={neu(t('v2.staffing_maske.feld.pt_gesamt'))}
        placeholder={t('v2.staffing_maske.feld.pt_gesamt')}
        className={`${eingabe} w-24`}
        value={pt}
        onChange={(e) => setPt(e.target.value)}
      />
      <textarea
        aria-label={t('v2.staffing_maske.neue_position_notiz', 'Notiz der neuen Position')}
        placeholder={t('v2.staffing_maske.feld.notiz_position')}
        className={`${eingabe} min-w-[12rem] flex-1`}
        rows={4}
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
      />
      <button
        type="button"
        className={`${KNOPF} disabled:cursor-not-allowed disabled:opacity-60`}
        style={{ color: 'var(--primary)' }}
        disabled={!vollstaendig || laeuft}
        onClick={anlegen}
      >
        {t('v2.staffing_maske.position_anlegen', 'Anlegen')}
      </button>
      <button
        type="button"
        className={KNOPF}
        style={{ color: 'var(--ink-3)' }}
        aria-label={t('v2.staffing_maske.neue_position_verwerfen', 'Neue Position verwerfen')}
        onClick={onFertig}
      >
        ✕
      </button>
    </fieldset>
  );
}
